/**
 * `@commerce/site-builder-blocks` — the shop's site-builder blocks: product listing, facets and
 * detail, the basket, checkout and order pages, click and collect, and the shopper's account.
 *
 * <p>They are the storefront's own, kept out of `@akademi/site-builder-blocks`, which the LMS
 * shares, and built from that package's public parts (`BlockRoot`, `AdapterPlaceholder`,
 * `useAsyncResource`, `sharedStyles`, `formatMoney`). Every block reads through
 * `useCommerceSource()` or `useCart()`, which the host mounts, and none calls `fetch`.
 *
 * <pre>{@code
 * import { genericBlocks } from '@akademi/site-builder-blocks';
 * import { commerceBlocks } from '@commerce/site-builder-blocks';
 *
 * const resolver = createBlockResolver({
 *   defaultBlocks: { ...genericBlocks, ...commerceBlocks },
 * });
 * }</pre>
 */

// — Components ——————————————————————————————————————————————————————
// Catalogue and basket. See `commerce/productQuery.ts` for the URL scheme ProductGrid and
// FacetPanel share, which keeps them in step with no prop wiring between them.
export { ProductGrid, type ProductGridProps } from './commerce/ProductGrid';
export { BrandStrip, type BrandStripProps } from './commerce/BrandStrip';
export { ProductDetail, type ProductDetailProps } from './commerce/ProductDetail';
export { SavedListPicker, type SavedListPickerSource, type SavedListSummary, type SavedListDetail as SavedListPickerDetail } from './commerce/SavedListPicker';
export { SavedListDetail, type SavedListDetailProps, type SavedListV2Source } from './commerce/SavedListDetail';
export { FacetPanel, type FacetPanelProps } from './commerce/FacetPanel';
export { KitEnquiryCta, type KitEnquiryCtaProps } from './commerce/KitEnquiryCta';
export { CartView, type CartViewProps } from './commerce/CartView';
export { SavedListsView, type SavedListsViewProps } from './commerce/SavedListsView';
export { CheckoutForm, type CheckoutFormProps } from './commerce/CheckoutForm';
export { OrderConfirmation, type OrderConfirmationProps } from './commerce/OrderConfirmation';
export { OrderTracking, type OrderTrackingProps } from './commerce/OrderTracking';
export { StoreLocator, type StoreLocatorProps } from './commerce/StoreLocator';
export { AddToCartButton, type AddToCartButtonProps } from './commerce/AddToCartButton';
export { TradeCounterHome, type TradeCounterHomeProps } from './commerce/TradeCounterHome';
export {
  DepartmentStoreHome,
  ElectricalTradeHome,
  EthicalMarketplaceHome,
  EverydayFitHome,
  HeritageSportHome,
  JobsiteSupplyHome,
  MinimalEssentialsHome,
  RoomDesignHome,
  type ReferenceHomeProps,
} from './commerce/ReferenceHomes';
// Shopper accounts.
export { SignInForm, type SignInFormProps } from './account/SignInForm';
export { RegisterForm, type RegisterFormProps } from './account/RegisterForm';
export { ActivateAccountForm, type ActivateAccountFormProps } from './account/ActivateAccountForm';
export { ForgotPasswordForm, type ForgotPasswordFormProps } from './account/ForgotPasswordForm';
export { ResetPasswordForm, type ResetPasswordFormProps } from './account/ResetPasswordForm';
export { ConfirmEmailChange, type ConfirmEmailChangeProps } from './account/ConfirmEmailChange';
export { AccountDashboard, type AccountDashboardProps } from './account/AccountDashboard';
export { AccountOrderDetail, type AccountOrderDetailProps } from './account/AccountOrderDetail';
export { AddressBook, type AddressBookProps } from './account/AddressBook';
export { TradeApplicationForm, type TradeApplicationFormProps } from './account/TradeApplicationForm';
export { InstallerApplicationForm, type InstallerApplicationFormProps } from './account/InstallerApplicationForm';
export { InstallerDocuments, type InstallerDocumentsProps } from './account/InstallerDocuments';
// What a host adds to its CommerceSource for trade accounts. See `account/tradeApplication.ts`.
export type {
  TradeApplicationMethods,
  TradeApplicationRequest,
  TradeApplicationState,
  TradeApplicationStatus,
  TradeBusinessType,
  TradeDocumentType,
  TradeDocumentUpload,
  TradeReferenceRequest,
  TradeRequirement,
  TradeRequirements,
  TradeSavings,
  TradeAccountSummary,
  TradeAccountMember,
} from './account/tradeApplication';
// The trade application's own logic, as VALUES rather than only as types, so a storefront
// theme can lay the form out its own way and still run the one implementation of what the
// form MEANS — what is required, what is missing, what request goes to the shop. A vertical
// that copied these would be a second validator drifting away from the server's.
export {
  EMPTY_REFERENCE_DRAFT,
  EMPTY_TRADE_DRAFT,
  NO_TRADE_REQUIREMENTS,
  TRADE_BUSINESS_TYPES,
  TRADE_DOCUMENT_TYPES,
  TRADE_ERROR_KINDS,
  TRADE_FIELD_LIMITS,
  TRADE_REQUIREMENTS,
  tradeApplicationRequest,
  tradeApplicationsOn,
  tradeDraftProblem,
  tradeErrorKind,
  tradeRequirementsOf,
  tradeSavingsOf,
  signInHrefFor,
} from './account/tradeApplication';
export type {
  TradeApplicationDraft,
  TradeCommerceSource,
  TradeDraftField,
  TradeDraftProblem,
  TradeErrorKind,
  TradeReferenceDraft,
} from './account/tradeApplication';
// The flow itself — the phase machine, the after-sign-up machine, submit — so the shared
// block and a theme's own block are one implementation with two layouts. See the file's note.
export { useTradeApplication } from './account/useTradeApplication';
export type {
  TradeAfterSignUp,
  TradeApplicationFlow,
  TradeApplicationPhase,
  TradeDocumentDraft,
  TradeFormError,
  TradeViewer,
  UseTradeApplicationOptions,
} from './account/useTradeApplication';
// The steps a guest's application passes through after their sign-up: the account's own
// fields, the emailed code, and the proof documents a sign-up could not carry.
export {
  ActivationCodeStep,
  EMPTY_GUEST_ACCOUNT,
  GuestAccountFields,
  TradeDocumentsUpload,
  guestAccountProblem,
} from './account/applyAtSignUp';
export type { GuestAccountDraft, GuestAccountProblem } from './account/applyAtSignUp';
// What a host adds to its CommerceSource for the shop's installers ("fitters" on a kitchen shop):
// applying, the documents page, and a signed-in installer's areas, jobs and quotes. The Spatial
// theme's fitter pages call the `InstallerMeMethods`. See `account/installer.ts`.
export type {
  InstallerAgreementToSign,
  InstallerApplicationMethods,
  InstallerApplicationRequest,
  InstallerApplicationStatus,
  InstallerBlocker,
  InstallerCertificateUpload,
  InstallerJob,
  InstallerJobQuote,
  InstallerJobStatus,
  InstallerMe,
  InstallerMeMethods,
  InstallerPolicyInput,
  InstallerPolicyType,
  InstallerQuoteRequest,
  InstallerQuoteStatus,
  InstallerRegistrations,
  InstallerRequirements,
  InstallerSelfMethods,
  InstallerSelfPolicy,
  InstallerSelfView,
  InstallerSigning,
  InstallerState,
  InstallerTrade,
  AreasReading,
} from './account/installer';
export {
  INSTALLER_TRADES,
  INSTALLER_POLICY_TYPES,
  readInstallerAreas,
  installerErrorKind,
} from './account/installer';
// What a host's `register` takes and answers beyond Akademi's contract: a password, and whether
// the account opened at once. See `account/registration.ts`.
export type { NewAccountRequest, SignUpOutcome } from './account/registration';
// Shared parts — exported so a theme (see `@commerce/theme-sorsy`) can build its own version of a
// block without re-deriving how a price, a picture or stock reads, or the ProductGrid/FacetPanel
// URL scheme.
export { ProductThumb } from './commerce/ProductThumb';
export { ProductPrice } from './commerce/ProductPrice';
export { AvailabilityBadge } from './commerce/AvailabilityBadge';
export {
  readProductQuery,
  patchProductParams,
  toggleFacetValue,
  isFacetValueSelected,
  hasActiveProductFilters,
  clearProductParams,
  type ProductQueryScope,
} from './commerce/productQuery';

export { COMMERCE_BLOCK_GROUPS } from './blockGroups';

// — Registry ———————————————————————————————————————————————————————
// `next/dynamic` per block, as `genericBlocks` does, so a page loads only the blocks it renders.
import dynamic from 'next/dynamic';
import type { BlockComponent } from '@akademi/site-builder-runtime';

const BrandStrip = dynamic(() =>
  import('./commerce/BrandStrip').then((m) => ({ default: m.BrandStrip })),
);
const ProductGrid = dynamic(() =>
  import('./commerce/ProductGrid').then((m) => ({ default: m.ProductGrid })),
);
const ProductDetail = dynamic(() =>
  import('./commerce/ProductDetail').then((m) => ({ default: m.ProductDetail })),
);
const FacetPanel = dynamic(() =>
  import('./commerce/FacetPanel').then((m) => ({ default: m.FacetPanel })),
);
const KitEnquiryCta = dynamic(() =>
  import('./commerce/KitEnquiryCta').then((m) => ({ default: m.KitEnquiryCta })),
);
const CartView = dynamic(() =>
  import('./commerce/CartView').then((m) => ({ default: m.CartView })),
);
const SavedListsView = dynamic(() =>
  import('./commerce/SavedListsView').then((m) => ({ default: m.SavedListsView })),
);
const CheckoutForm = dynamic(() =>
  import('./commerce/CheckoutForm').then((m) => ({ default: m.CheckoutForm })),
);
const OrderConfirmation = dynamic(() =>
  import('./commerce/OrderConfirmation').then((m) => ({ default: m.OrderConfirmation })),
);
const OrderTracking = dynamic(() =>
  import('./commerce/OrderTracking').then((m) => ({ default: m.OrderTracking })),
);
const StoreLocator = dynamic(() =>
  import('./commerce/StoreLocator').then((m) => ({ default: m.StoreLocator })),
);
const AddToCartButton = dynamic(() =>
  import('./commerce/AddToCartButton').then((m) => ({ default: m.AddToCartButton })),
);
const TradeCounterHome = dynamic(() =>
  import('./commerce/TradeCounterHome').then((m) => ({ default: m.TradeCounterHome })),
);
const EverydayFitHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.EverydayFitHome })),
);
const DepartmentStoreHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.DepartmentStoreHome })),
);
const EthicalMarketplaceHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.EthicalMarketplaceHome })),
);
const ElectricalTradeHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.ElectricalTradeHome })),
);
const HeritageSportHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.HeritageSportHome })),
);
const MinimalEssentialsHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.MinimalEssentialsHome })),
);
const RoomDesignHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.RoomDesignHome })),
);
const JobsiteSupplyHome = dynamic(() =>
  import('./commerce/ReferenceHomes').then((m) => ({ default: m.JobsiteSupplyHome })),
);
const SignInForm = dynamic(() =>
  import('./account/SignInForm').then((m) => ({ default: m.SignInForm })),
);
const RegisterForm = dynamic(() =>
  import('./account/RegisterForm').then((m) => ({ default: m.RegisterForm })),
);
const ActivateAccountForm = dynamic(() =>
  import('./account/ActivateAccountForm').then((m) => ({ default: m.ActivateAccountForm })),
);
const ForgotPasswordForm = dynamic(() =>
  import('./account/ForgotPasswordForm').then((m) => ({ default: m.ForgotPasswordForm })),
);
const ResetPasswordForm = dynamic(() =>
  import('./account/ResetPasswordForm').then((m) => ({ default: m.ResetPasswordForm })),
);
const ConfirmEmailChange = dynamic(() =>
  import('./account/ConfirmEmailChange').then((m) => ({ default: m.ConfirmEmailChange })),
);
const AccountDashboard = dynamic(() =>
  import('./account/AccountDashboard').then((m) => ({ default: m.AccountDashboard })),
);
const AccountOrderDetail = dynamic(() =>
  import('./account/AccountOrderDetail').then((m) => ({ default: m.AccountOrderDetail })),
);
const AddressBook = dynamic(() =>
  import('./account/AddressBook').then((m) => ({ default: m.AddressBook })),
);
const TradeApplicationForm = dynamic(() =>
  import('./account/TradeApplicationForm').then((m) => ({ default: m.TradeApplicationForm })),
);
const InstallerApplicationForm = dynamic(() =>
  import('./account/InstallerApplicationForm').then((m) => ({ default: m.InstallerApplicationForm })),
);
const InstallerDocuments = dynamic(() =>
  import('./account/InstallerDocuments').then((m) => ({ default: m.InstallerDocuments })),
);

/**
 * Spread after `genericBlocks` into {@code createBlockResolver({ defaultBlocks })}. Maps each block
 * type id — the backend's {@code BlockDefinition.type}, seeded from `../seed-data/blocks` — to the
 * component that renders it.
 */
export const commerceBlocks: Record<string, BlockComponent> = {
  // Catalogue and basket
  ProductGrid: ProductGrid as unknown as BlockComponent,
  BrandStrip: BrandStrip as unknown as BlockComponent,
  ProductDetail: ProductDetail as unknown as BlockComponent,
  FacetPanel: FacetPanel as unknown as BlockComponent,
  KitEnquiryCta: KitEnquiryCta as unknown as BlockComponent,
  CartView: CartView as unknown as BlockComponent,
  SavedListsView: SavedListsView as unknown as BlockComponent,
  CheckoutForm: CheckoutForm as unknown as BlockComponent,
  OrderConfirmation: OrderConfirmation as unknown as BlockComponent,
  OrderTracking: OrderTracking as unknown as BlockComponent,
  StoreLocator: StoreLocator as unknown as BlockComponent,
  AddToCartButton: AddToCartButton as unknown as BlockComponent,
  TradeCounterHome: TradeCounterHome as unknown as BlockComponent,
  EverydayFitHome: EverydayFitHome as unknown as BlockComponent,
  DepartmentStoreHome: DepartmentStoreHome as unknown as BlockComponent,
  EthicalMarketplaceHome: EthicalMarketplaceHome as unknown as BlockComponent,
  ElectricalTradeHome: ElectricalTradeHome as unknown as BlockComponent,
  HeritageSportHome: HeritageSportHome as unknown as BlockComponent,
  MinimalEssentialsHome: MinimalEssentialsHome as unknown as BlockComponent,
  RoomDesignHome: RoomDesignHome as unknown as BlockComponent,
  JobsiteSupplyHome: JobsiteSupplyHome as unknown as BlockComponent,
  // Accounts
  SignInForm: SignInForm as unknown as BlockComponent,
  RegisterForm: RegisterForm as unknown as BlockComponent,
  ActivateAccountForm: ActivateAccountForm as unknown as BlockComponent,
  ForgotPasswordForm: ForgotPasswordForm as unknown as BlockComponent,
  ResetPasswordForm: ResetPasswordForm as unknown as BlockComponent,
  ConfirmEmailChange: ConfirmEmailChange as unknown as BlockComponent,
  AccountDashboard: AccountDashboard as unknown as BlockComponent,
  AccountOrderDetail: AccountOrderDetail as unknown as BlockComponent,
  AddressBook: AddressBook as unknown as BlockComponent,
  TradeApplicationForm: TradeApplicationForm as unknown as BlockComponent,
  InstallerApplicationForm: InstallerApplicationForm as unknown as BlockComponent,
  InstallerDocuments: InstallerDocuments as unknown as BlockComponent,
};
